import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getMyOrganizations } from "@/lib/api/account";
import { notifyError, dismissToast } from "@/lib/notify.jsx";

// Fetch user organizations thunk
export const fetchUserOrganizations = createAsyncThunk(
  "orgs/fetchUserOrganizations",
  async (arg = {}, { rejectWithValue, getState }) => {
    const { accessToken } = arg;
    const prevToastId = getState().orgs.toastIds.fetchUserOrganizations;
    if (prevToastId) dismissToast(prevToastId);

    try {
      const res = await getMyOrganizations(accessToken);
      return Array.isArray(res.data?.data) ? res.data.data : res.data;
    } catch (err) {
      const description =
        err?.response?.data?.error_description ||
        "pages.auth_settings.login.failed_to_load_organizations";
      const toastId = notifyError(description, { duration: 8000 });
      return rejectWithValue({ error: description, toastId });
    }
  },
);

const orgSlice = createSlice({
  name: "orgs",
  initialState: {
    list: [],
    loading: false,
    error: null,
    toastIds: {
      fetchUserOrganizations: null,
    },
    errors: {
      fetchUserOrganizations: null,
    },
    current: {
      logoUrl: null,
      companyName: null,
      creationDate: null,
      ownerFirstName: null,
      ownerLastName: null,
      links: {
        amainvoiceLink: null,
        billbeeLink: null,
        replycoLink: null,
        ventoryOneLink: null,
      },
    },
  },
  reducers: {
    clearOrgs: (state) => {
      state.list = [];
      state.current.logoUrl = null;
      state.current.companyName = null;
      state.current.creationDate = null;
      state.current.ownerFirstName = null;
      state.current.ownerLastName = null;
      state.current.links.amainvoiceLink = null;
      state.current.links.billbeeLink = null;
      state.current.links.replycoLink = null;
      state.current.links.ventoryOneLink = null;
      state.errors.fetchUserOrganizations = null;
      state.toastIds.fetchUserOrganizations = null;
    },
    setLogoUrl: (state, action) => {
      state.current.logoUrl = action.payload;
    },
    setCompanyName: (state, action) => {
      state.current.companyName = action.payload;
    },
    setOrganizationCreationDate: (state, action) => {
      state.current.creationDate = action.payload;
    },
    setOwnerName: (state, action) => {
      state.current.ownerFirstName = action.payload.firstName;
      state.current.ownerLastName = action.payload.lastName;
    },
    setAmainvoiceLink: (state, action) => {
      state.current.links.amainvoiceLink = action.payload;
    },
    setBillbeeLink: (state, action) => {
      state.current.links.billbeeLink = action.payload;
    },
    setReplycoLink: (state, action) => {
      state.current.links.replycoLink = action.payload;
    },
    setVentoryOneLink: (state, action) => {
      state.current.links.ventoryOneLink = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserOrganizations.pending, (state) => {
        state.loading = true;
        state.errors.fetchUserOrganizations = null;
      })
      .addCase(fetchUserOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.errors.fetchUserOrganizations = null;
        state.toastIds.fetchUserOrganizations = null;
      })
      .addCase(fetchUserOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.errors.fetchUserOrganizations = action.payload?.error || null;
        state.toastIds.fetchUserOrganizations = action.payload?.toastId || null;
      });
  },
});

export const {
  clearOrgs,
  setLogoUrl,
  setCompanyName,
  setOrganizationCreationDate,
  setOwnerName,
  setAmainvoiceLink,
  setBillbeeLink,
  setReplycoLink,
  setVentoryOneLink,
} = orgSlice.actions;
export default orgSlice.reducer;
